// /recordings — Past Recordings View redesign (LLD 13).
//
// Three-region investigation surface: center (player + transport + timeline),
// right (camera scope rail). Replaces the old enterprise-broken page that
// fired one events:search with every doorbot id (`400 too_many_ids`).
//
// Data flow:
//   1. Boot loads /api/locations + walks /api/cameras pagination -> scope rail.
//   2. The user picks cameras (≤12 on the timeline, ≤4 in the grid).
//   3. Per-camera events are fetched via /api/cameras/{id}/events (single)
//      or POST /api/events:search (multi, batched ≤50).
//   4. Clicking an event glyph or dragging a range loads a stitched clip
//      via POST /api/clips:resolve; the player double-buffers the segments.
//
// Nothing fires until cameras are selected. The events:search batcher
// chunks at MAX_SEARCH_IDS=50 (LLD 13 §11) — never one giant body.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ApiError, api } from "@/api";
import { useToast } from "@/components";
import { useRole } from "@/role";
import { capabilitiesFor } from "@/role/capabilities";
import { cameraLocationsClient } from "@/api/cameraLocationsClient";
import { camerasClient, type Camera } from "@/api/camerasClient";
import {
  eventsClient,
  type CameraEvent,
} from "@/api/eventsClient";
import { clipsClient, type ClipTier } from "@/api/clipsClient";

import { GridStage } from "./components/GridStage";
import { PlayerStage } from "./components/PlayerStage";
import { ScopeRail, type ScopeLocation } from "./components/ScopeRail";
import { Timeline } from "./components/Timeline";
import {
  TransportBar,
  type ClipInfo,
  type ViewMode,
} from "./components/TransportBar";

import {
  KIND_ORDER,
  KINDS,
  visualKindFor,
  type EventKindId,
} from "./helpers/kinds";
import { xOf } from "./helpers/window";
import { useGridEngine } from "./hooks/useGridEngine";
import {
  CAM_CAP,
  type ScopeCamera,
  useScopeSelection,
} from "./hooks/useScopeSelection";
import { useStitchPlayer, type ClipMeta } from "./hooks/useStitchPlayer";
import { useTimelineWindow } from "./hooks/useTimelineWindow";

import "./recordings.css";

const HEALTH_DEFAULT_CONSENT_DAYS = 3;
const EVENT_SEARCH_MAX_IDS = 50;
const TIMELINE_MIN_PX = 168;

interface HealthState {
  nowMs: number;
  consentAtMs: number;
}

function describeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return `${err.status} ${err.code ?? err.message}`;
  if (err instanceof Error) return err.message;
  return fallback;
}

interface HealthResponse {
  ok?: boolean;
  now_ms?: number;
  consent_at_ms?: number;
}

/**
 * Best-effort /api/health probe. Production may not expose this endpoint
 * yet; on any failure we fall back to `Date.now()` and a 3-day consent
 * floor so the surface still renders. (LLD 13 — graceful degrade pattern
 * mirrored from the prototype `LIVE` probe.)
 */
async function probeHealth(): Promise<HealthState> {
  const fallback: HealthState = {
    nowMs: Date.now(),
    consentAtMs: Date.now() - HEALTH_DEFAULT_CONSENT_DAYS * 24 * 60 * 60 * 1000,
  };
  try {
    // /api/health is an unauthenticated probe — `skipActingRole` keeps the
    // wrapper from kicking us to the role-picker before the surface boots.
    const h = await api.get<HealthResponse>("/api/health", {
      skipActingRole: true,
    });
    if (typeof h.now_ms === "number" && typeof h.consent_at_ms === "number") {
      return { nowMs: h.now_ms, consentAtMs: h.consent_at_ms };
    }
    return fallback;
  } catch {
    return fallback;
  }
}

/**
 * Build the right-rail location groups by joining /api/locations with the
 * paginated /api/cameras list. Cameras whose location_id is unknown are
 * silently dropped — same as the prototype.
 */
async function loadTenant(
  signal: AbortSignal,
): Promise<{ locations: ScopeLocation[]; allCameras: Camera[] }> {
  const [locsRaw, cams] = await Promise.all([
    cameraLocationsClient.list(signal),
    camerasClient.listAll({ signal }),
  ]);
  const byId = new Map<
    string,
    {
      location_id: string;
      display_name: string;
      address?: string | null;
      cameras: ScopeCamera[];
      online: boolean;
      open: boolean;
    }
  >();
  for (const l of locsRaw) {
    byId.set(l.location_id, {
      location_id: l.location_id,
      display_name: l.display_name,
      address: l.address ?? null,
      cameras: [],
      online: true,
      open: false,
    });
  }
  for (const c of cams) {
    const loc = byId.get(c.location_id);
    if (!loc) continue;
    const desc = c.description ?? "";
    // The prototype trims leading "TENANT - LOC - " prefix from the
    // description so the chip shows just the camera name.
    const parts = desc.split(" - ");
    const short = parts.length >= 3 ? parts.slice(2).join(" - ") : desc || c.doorbot_id;
    loc.cameras.push({
      doorbot_id: c.doorbot_id,
      location_id: c.location_id,
      name: short,
      // The list endpoint can include `has_real_footage` (camera-domain
      // shape, when the BFF surfaces it). Fall back to false; UI uses this
      // only to color the per-lane coverage band.
      has_real:
        (c as Camera & { has_real_footage?: boolean }).has_real_footage === true,
      online: c.status === "online",
    });
  }
  return {
    locations: [...byId.values()],
    allCameras: cams,
  };
}

/**
 * Chunk the doorbot list into ≤50-id batches and run events:search per batch.
 * Returns {byCamera: Map<id, events>, denied: number}. This is THE core
 * fix from the old page — the backend rejects > 50 ids with too_many_ids.
 */
async function searchEventsBatched(
  doorbotIds: readonly string[],
  startIso: string,
  endIso: string,
  signal: AbortSignal,
): Promise<{ byCamera: Map<string, CameraEvent[]>; denied: number }> {
  const byCamera = new Map<string, CameraEvent[]>();
  let denied = 0;
  for (let i = 0; i < doorbotIds.length; i += EVENT_SEARCH_MAX_IDS) {
    const chunk = doorbotIds.slice(i, i + EVENT_SEARCH_MAX_IDS);
    const r = await eventsClient.search(
      {
        doorbot_ids: chunk,
        start: startIso,
        end: endIso,
        limit: 200,
      },
      signal,
    );
    for (const ev of r.items ?? []) {
      const list = byCamera.get(ev.doorbot_id);
      if (list) list.push(ev);
      else byCamera.set(ev.doorbot_id, [ev]);
    }
    denied += (r.denied ?? []).length;
    // Initialise empty lists for ids that came back with zero events so
    // callers don't refetch them.
    for (const id of chunk) {
      if (!byCamera.has(id)) byCamera.set(id, []);
    }
  }
  return { byCamera, denied };
}

export function RecordingsPage(): JSX.Element {
  const { currentRole } = useRole();
  const toast = useToast();
  const caps = useMemo(() => capabilitiesFor(currentRole), [currentRole]);

  const scope = useScopeSelection();
  const player = useStitchPlayer();
  const grid = useGridEngine();

  // Tenant + health load.
  const [tenant, setTenant] = useState<{
    locations: ScopeLocation[];
    allCameras: Camera[];
  } | null>(null);
  const [tenantError, setTenantError] = useState<string | null>(null);
  const [health, setHealth] = useState<HealthState>(() => ({
    nowMs: Date.now(),
    consentAtMs: Date.now() - HEALTH_DEFAULT_CONSENT_DAYS * 24 * 60 * 60 * 1000,
  }));
  const [eventsByCamera, setEventsByCamera] = useState<
    ReadonlyMap<string, readonly CameraEvent[]>
  >(() => new Map());
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [scopeSearch, setScopeSearch] = useState("");
  const [openLocs, setOpenLocs] = useState<Record<string, boolean>>({});

  // Window state — relative 24h by default.
  const win = useTimelineWindow(health.nowMs);

  // View + filter state.
  const [view, setView] = useState<ViewMode>("single");
  const [kindFilter, setKindFilter] = useState<ReadonlySet<EventKindId>>(
    () => new Set(KIND_ORDER),
  );
  const [personOnly, setPersonOnly] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [tlHeightPx, setTlHeightPx] = useState<number>(300);
  // Track grid-loaded range separately so cliprange band can render in grid mode.
  const [gridRange, setGridRange] = useState<{ startMs: number; durMs: number } | null>(null);

  // -------------------------------------------------------------- boot
  useEffect(() => {
    if (currentRole === null) return;
    const ctrl = new AbortController();
    void (async () => {
      const h = await probeHealth();
      if (!ctrl.signal.aborted) setHealth(h);
    })();
    void (async () => {
      try {
        const t = await loadTenant(ctrl.signal);
        if (ctrl.signal.aborted) return;
        setTenant(t);
        // Default the first location open so the operator sees a populated
        // list rather than just headers.
        const first = t.locations[0];
        if (first) setOpenLocs({ [first.location_id]: true });
      } catch (err) {
        if (ctrl.signal.aborted) return;
        setTenantError(describeError(err, "Failed to load tenant"));
      }
    })();
    return () => ctrl.abort();
  }, [currentRole]);

  // -------------------------------------------------------------- timeline-resize
  useEffect(() => {
    document.documentElement.style.setProperty("--rec-tl-h", `${tlHeightPx}px`);
  }, [tlHeightPx]);

  const tlResizeRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const handle = tlResizeRef.current;
    if (!handle) return;
    const onDown = (e: MouseEvent): void => {
      e.preventDefault();
      const startY = e.clientY;
      const startH = tlHeightPx;
      handle.classList.add("dragging");
      document.body.classList.add("rec-tl-resizing");
      const max = Math.round((window.innerHeight - 56) * 0.78);
      const onMove = (ev: MouseEvent): void => {
        const px = Math.max(TIMELINE_MIN_PX, Math.min(max, startH + (startY - ev.clientY)));
        setTlHeightPx(px);
      };
      const onUp = (): void => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        handle.classList.remove("dragging");
        document.body.classList.remove("rec-tl-resizing");
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    };
    handle.addEventListener("mousedown", onDown);
    return () => handle.removeEventListener("mousedown", onDown);
  }, [tlHeightPx]);

  // -------------------------------------------------------------- events fetch
  // Selected ids — kept stable so the deps array doesn't churn.
  const selectedIds = useMemo(
    () => [...scope.selected.keys()].sort(),
    [scope.selected],
  );
  const selectedIdsKey = selectedIds.join(",");
  const winStartMs = win.win.start;
  const winEndMs = win.win.end;

  useEffect(() => {
    if (selectedIds.length === 0) {
      setEventsByCamera(new Map());
      return;
    }
    const ctrl = new AbortController();
    setEventsError(null);
    void (async () => {
      try {
        // Always query against the consent-bounded window so glyphs cover
        // everything authorized; the timeline window only affects the
        // *visible* slice. This matches the prototype's behavior.
        const startIso = new Date(health.consentAtMs).toISOString();
        const endIso = new Date(health.nowMs).toISOString();
        if (selectedIds.length === 1) {
          const id = selectedIds[0]!;
          const r = await eventsClient.listForCamera(
            id,
            { start: startIso, end: endIso, signal: ctrl.signal },
          );
          setEventsByCamera(new Map([[id, r.items ?? []]]));
        } else {
          const { byCamera, denied } = await searchEventsBatched(
            selectedIds,
            startIso,
            endIso,
            ctrl.signal,
          );
          setEventsByCamera(byCamera);
          if (denied > 0) {
            toast.info({
              title: `${denied} camera${denied === 1 ? "" : "s"} denied`,
              description: "Insufficient grants — switch roles to widen scope.",
            });
          }
        }
      } catch (err) {
        if (ctrl.signal.aborted) return;
        setEventsError(describeError(err, "Failed to load events"));
      }
    })();
    return () => ctrl.abort();
  // We deliberately depend on selectedIdsKey (the joined ids) so a stable
  // selection doesn't refire on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIdsKey, health.nowMs, health.consentAtMs, toast]);

  // Window UI consumes the live window for ticks/glyphs but isolated for clarity.
  void winStartMs;
  void winEndMs;

  // -------------------------------------------------------------- event handlers
  const onSearchChange = useCallback((q: string) => setScopeSearch(q), []);
  const onToggleOpen = useCallback((id: string) => {
    setOpenLocs((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const updateGridFromScope = useCallback(() => {
    if (view !== "grid") return;
    const cams: ScopeCamera[] = [];
    for (const id of scope.gridSel) {
      const c = scope.selected.get(id);
      if (c) cams.push(c);
    }
    grid.setCells(cams);
  }, [grid, scope.gridSel, scope.selected, view]);

  useEffect(() => {
    updateGridFromScope();
  }, [updateGridFromScope]);

  const onToggleCamera = useCallback(
    (cam: ScopeCamera, on: boolean) => {
      const ok = scope.toggleCamera(cam, on);
      if (!ok) {
        toast.warning({
          title: "Timeline cap reached",
          description: `You can have up to ${CAM_CAP} cameras on the timeline.`,
        });
      }
    },
    [scope, toast],
  );
  const onToggleLocation = useCallback(
    (cams: readonly ScopeCamera[], add: boolean) => scope.toggleLocation(cams, add),
    [scope],
  );

  // Wallclock helpers for timeline scrubbing & playhead positioning.
  const playheadFrac = useMemo<number | null>(() => {
    let baseWall: number | null = null;
    if (view === "grid" && grid.startMs > 0) {
      baseWall = grid.startMs + (grid.virtualMs ?? 0);
    } else if (view === "single" && player.clip) {
      baseWall = player.clip.startMs + (player.clip.virtualMs ?? 0);
    }
    if (baseWall === null) return null;
    const f = xOf(baseWall, win.win);
    if (f < -0.02 || f > 1.02) return null;
    return Math.max(0, Math.min(1, f));
  }, [view, grid.startMs, grid.virtualMs, player.clip, win.win]);

  const onScrub = useCallback(
    (wallMs: number, dragging: boolean) => {
      if (view === "grid") {
        if (grid.cells.length === 0 || grid.durMs === 0) return;
        const v = wallMs - grid.startMs;
        if (!dragging) {
          // Resume on release if we were playing before scrub started.
          grid.seekVirtual(v);
        } else {
          grid.seekVirtual(v);
        }
        return;
      }
      if (!player.clip) return;
      const v = wallMs - player.clip.startMs;
      player.seekVirtual(v, !dragging && player.clip.playing);
    },
    [view, grid, player],
  );

  const onClickEvent = useCallback(
    (ev: CameraEvent, cam: ScopeCamera) => {
      setSelectedEventId(ev.ding_id);
      const meta: ClipMeta = {
        doorbot_id: ev.doorbot_id || cam.doorbot_id,
        name: cam.name,
        locId: cam.location_id,
      };
      const dur = Math.max(30_000, ev.duration_seconds * 1000);
      win.focusOn(ev.start_ms, dur);
      if (view === "grid") {
        setGridRange({ startMs: ev.start_ms, durMs: dur });
        void grid.loadAll(ev.start_ms, dur);
        return;
      }
      void player.load(meta, ev.start_ms, dur);
    },
    [grid, player, view, win],
  );

  const onSelectRange = useCallback(
    (cam: ScopeCamera, t1: number, t2: number) => {
      setSelectedEventId(null);
      const meta: ClipMeta = {
        doorbot_id: cam.doorbot_id,
        name: cam.name,
        locId: cam.location_id,
      };
      const dur = t2 - t1;
      win.focusOn(t1, dur);
      if (view === "grid") {
        setGridRange({ startMs: t1, durMs: dur });
        void grid.loadAll(t1, dur);
        return;
      }
      void player.load(meta, t1, dur);
    },
    [grid, player, view, win],
  );

  const onSwitchCamera = useCallback(
    (id: string) => {
      const cam = scope.selected.get(id);
      if (!cam) return;
      if (view !== "single") return;
      if (player.clip && player.clip.meta.doorbot_id === id) return;
      setSelectedEventId(null);
      const meta: ClipMeta = {
        doorbot_id: id,
        name: cam.name,
        locId: cam.location_id,
      };
      if (!player.clip) {
        const span = win.win.end - win.win.start;
        void player.load(meta, win.win.start, span);
        return;
      }
      const at = player.clip.virtualMs ?? 0;
      const wasPlaying = player.clip.playing;
      void player.load(meta, player.clip.startMs, player.clip.durMs, {
        keepWindow: true,
        seekMs: at,
        keepPlaying: wasPlaying,
      });
    },
    [player, scope.selected, view, win.win.end, win.win.start],
  );

  const onChangeView = useCallback(
    (v: ViewMode) => {
      setView(v);
      if (v === "grid") {
        scope.initGrid();
        // Stop the single player; the grid engine takes over.
        player.pause();
      } else {
        // Leaving grid -> stop grid playback so timers don't keep firing.
        grid.pause();
      }
    },
    [grid, player, scope],
  );

  const onPromoteCell = useCallback(
    (cellIndex: number) => {
      const cell = grid.cells[cellIndex];
      if (!cell) return;
      const cam = cell.cam;
      const startMs = grid.startMs;
      const durMs = grid.durMs;
      if (startMs <= 0 || durMs <= 0) return;
      setView("single");
      const meta: ClipMeta = {
        doorbot_id: cam.doorbot_id,
        name: cam.name,
        locId: cam.location_id,
      };
      void player.load(meta, startMs, durMs, {
        seekMs: grid.virtualMs,
        keepPlaying: false,
      });
    },
    [grid.cells, grid.durMs, grid.startMs, grid.virtualMs, player],
  );

  // 416-gap surfacing.
  const surfacedGapRef = useRef<typeof player.lastGap>(null);
  useEffect(() => {
    if (!player.lastGap) return;
    if (surfacedGapRef.current === player.lastGap) return;
    surfacedGapRef.current = player.lastGap;
    const { startMs, jumpedToMs } = player.lastGap;
    const at = new Date(startMs).toLocaleTimeString();
    const to =
      jumpedToMs !== null ? new Date(jumpedToMs).toLocaleTimeString() : null;
    toast.info({
      title: to ? `No footage — jumped to ${to}` : `No footage at ${at}`,
      description: to
        ? "The camera wasn't recording then; advanced to the next available footage."
        : "Camera wasn't recording during this range.",
    });
    player.clearGap();
  }, [player, toast]);

  // -------------------------------------------------------------- clip-info
  const cameras = useMemo(
    () => [...scope.selected.values()],
    [scope.selected],
  );

  const clipInfo: ClipInfo | null = useMemo(() => {
    const exportEnabled = caps.canDownload;
    const reason = caps.canDownload
      ? undefined
      : "Download requires the recording:export permission.";
    if (view === "grid" && gridRange) {
      const n = grid.cells.length;
      return {
        title: `${n} camera${n !== 1 ? "s" : ""} · synced`,
        sub: `${new Date(gridRange.startMs).toLocaleString()} · ${(gridRange.durMs / 60_000).toFixed(0)} min`,
        badges: (
          <>
            <span className="rec-badge">2×2 grid</span>
            <span className="rec-badge info">SD only</span>
          </>
        ),
        requestBody: `${n}× POST /api/clips:resolve (parallel)
→ tier: scan · avc 360p · 5fps
→ ≈3 MB/min each · ${n}× synced clock`,
        exportLabel: `Export all`,
        exportEnabled,
        exportDisabledReason: reason,
        onExport: () => {
          toast.info({
            title: `Queued ${n} export${n === 1 ? "" : "s"}`,
            description: "Bulk export will fan out one job per camera.",
          });
        },
      };
    }
    if (view === "single" && player.clip) {
      const c = player.clip;
      const k = visualKindFor({ kind: "motion", person_detected: false });
      return {
        title: c.meta.name,
        sub: `${c.meta.locId ?? ""} · ${new Date(c.startMs).toLocaleString()} · ${(c.durMs / 60_000).toFixed(0)} min · ${c.segments.length} seg${c.segments.length > 1 ? "s" : ""}`,
        badges: (
          <>
            <span className="rec-badge">
              {c.tier === "hd" ? "HD" : "SD"}
            </span>
            {k && (
              <span className="rec-badge info">
                <span className={`gl ${KINDS[k].cls}`} aria-hidden="true" />
                {KINDS[k].label}
              </span>
            )}
          </>
        ),
        requestBody: `POST /api/clips:resolve
→ device: ${c.meta.name}
→ codec: avc · ${c.tier === "hd" ? "1080p · 25fps" : "360p · 5fps"}
→ ${c.segments.length} segment${c.segments.length > 1 ? "s" : ""} · ≤10 min each`,
        exportLabel: "Export",
        exportEnabled,
        exportDisabledReason: reason,
        onExport: () => {
          toast.info({
            title: "Queued export",
            description: `Saving ${c.segments.length} segment${c.segments.length > 1 ? "s" : ""}.`,
          });
        },
      };
    }
    return null;
  }, [view, gridRange, grid.cells.length, player.clip, caps.canDownload, toast]);

  // ---------------- Locations dressed for the scope rail.
  const railLocations: ScopeLocation[] = useMemo(() => {
    if (!tenant) return [];
    return tenant.locations.map((l) => ({
      ...l,
      open: openLocs[l.location_id] ?? false,
    }));
  }, [tenant, openLocs]);

  // ---------------- The combined "loaded range" cliprange band reads.
  const loadedRange =
    view === "grid"
      ? gridRange
      : player.clip
        ? { startMs: player.clip.startMs, durMs: player.clip.durMs }
        : null;
  const loadedSegments =
    view === "single" && player.clip ? player.clip.segments : [];

  const onTogglePlay = useCallback(() => {
    if (view === "grid") grid.toggle();
    else player.toggle();
  }, [grid, player, view]);

  const onExtend = useCallback(
    (direction: -1 | 1) => {
      if (view === "grid") void grid.extend(direction);
      else void player.extend(direction);
    },
    [grid, player, view],
  );

  const onChangeSpeed = useCallback(
    (s: number) => {
      if (view === "grid") grid.setSpeed(s);
      else player.setSpeed(s);
    },
    [grid, player, view],
  );

  const onChangeTier = useCallback(
    (t: ClipTier) => {
      if (view === "grid") return; // grid is SD-only
      player.setTier(t);
    },
    [player, view],
  );

  const onScopeDismiss = useCallback(() => scope.clearCapBanner(), [scope]);

  // The grid engine and player share `view` to coordinate playhead drive.
  void clipsClient; // silence unused-import warnings on the typed re-export above

  return (
    <section className="rec-surface">
      <section className="rec-col rec-center" aria-label="Player and timeline">
        {tenantError && (
          <div className="rec-banner danger" role="alert">
            <strong>Failed to load cameras:</strong> {tenantError}
          </div>
        )}
        {eventsError && (
          <div className="rec-banner" role="status">
            <strong>Some events failed to load:</strong> {eventsError}
          </div>
        )}
        <div className="rec-stagewrap">
          {view === "single" ? (
            <PlayerStage
              clip={player.clip}
              loading={player.loading}
              attach={player.attach}
              osdWallMs={
                player.clip
                  ? player.clip.startMs + (player.clip.virtualMs ?? 0)
                  : null
              }
            />
          ) : (
            <GridStage
              cells={grid.cells}
              selectedIndex={grid.selectedIndex}
              perCellWallMs={
                grid.startMs > 0 ? grid.startMs + (grid.virtualMs ?? 0) : null
              }
              attach={grid.attach}
              onSelectCell={grid.selectCell}
              onPromoteCell={onPromoteCell}
            />
          )}
        </div>
        <TransportBar
          playing={view === "grid" ? grid.playing : player.clip?.playing ?? false}
          hasLoaded={view === "grid" ? grid.cells.length > 0 && grid.startMs > 0 : player.clip !== null}
          view={view}
          speed={view === "grid" ? grid.speed : player.speed}
          tier={view === "grid" ? "scan" : player.tier}
          hdDisabled={view === "grid" ? true : player.clip?.hdDisabled ?? false}
          clipInfo={clipInfo}
          onTogglePlay={onTogglePlay}
          onExtend={onExtend}
          onChangeSpeed={onChangeSpeed}
          onChangeTier={onChangeTier}
          onChangeView={onChangeView}
        />
        <Timeline
          cameras={cameras}
          eventsByCamera={eventsByCamera}
          gridMode={view === "grid"}
          gridCameras={scope.gridSel}
          loadedCameraId={view === "single" ? player.clip?.meta.doorbot_id ?? null : null}
          loadedRange={loadedRange}
          loadedSegments={loadedSegments}
          nowMs={health.nowMs}
          consentAtMs={health.consentAtMs}
          window={win}
          selectedEventId={selectedEventId}
          kindFilter={kindFilter}
          personOnly={personOnly}
          playheadFrac={playheadFrac}
          onToggleKind={(k) =>
            setKindFilter((prev) => {
              const next = new Set(prev);
              if (next.has(k)) next.delete(k);
              else next.add(k);
              return next;
            })
          }
          onTogglePersonOnly={() => setPersonOnly((p) => !p)}
          onClickEvent={onClickEvent}
          onSelectRange={onSelectRange}
          onSwitchCamera={onSwitchCamera}
          onToggleGrid={scope.toggleGrid}
          onScrub={onScrub}
        />
        <div
          ref={tlResizeRef}
          className="rec-tl-resize"
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize timeline"
          tabIndex={0}
          onKeyDown={(e) => {
            const max = Math.round((window.innerHeight - 56) * 0.78);
            if (e.key === "ArrowUp") setTlHeightPx((h) => Math.min(max, h + 24));
            if (e.key === "ArrowDown") setTlHeightPx((h) => Math.max(TIMELINE_MIN_PX, h - 24));
          }}
        />
      </section>
      <ScopeRail
        locations={railLocations}
        selected={scope.selected}
        gridSel={scope.gridSel}
        inGridView={view === "grid"}
        searchQuery={scopeSearch}
        capReached={scope.capReached}
        onSearchChange={onSearchChange}
        onToggleCamera={onToggleCamera}
        onToggleLocation={onToggleLocation}
        onToggleOpen={onToggleOpen}
        onDismissCap={onScopeDismiss}
      />
    </section>
  );
}

